// Knockout.js
// Knockout Mode overview — web port of KnockoutOverviewView.swift.
//
// Renders LAST POOL / CURRENT POOL / NEXT POOL / HOW TO PLAY from live Firestore
// data, plus the pinned DRAFT NOW and REGISTER buttons. Each read-derived button
// state (draftNow / drafted / eliminated / notPlaying) comes straight from the
// user's standing and the pool-day status.
//
// Live updates: rather than hold an open Firestore listener (the iOS approach via
// KnockoutManager.cutLineListener), the overview re-reads the same pool / pool-day
// / standing docs on a light interval. The cut line + alive count refresh
// server-side every 10 min (refreshKnockoutCutLines) and other users' scores move
// throughout the draft window, so polling keeps EST. CUT, ALIVE, MY SCORE and the
// "updated X min ago" label live. The poll pauses while the tab is hidden and
// refreshes immediately on return; it never toggles the loading overlay or
// collapses an open "Today's Top Scores" list.
//
// Every Knockout read requires a real signed-in account (Firestore rules), so
// guests get a sign-in teaser instead of live data.

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import { useKnockoutSession } from "../managers/KnockoutContext";
import {
  loadActivePools,
  loadLastCompletedPool,
  loadPoolDay,
  loadPlayerPool,
  loadStanding,
  loadStandings,
  isRegistered,
  countRegistrants,
  registerForKnockout,
  unregisterFromKnockout,
} from "../managers/knockoutService";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import trophyFill from "../assets/trophy-fill.svg";
import KnockoutChampionsModal from "../components/KnockoutChampionsModal";
import {
  styles,
  LastPoolSection,
  CurrentPoolSection,
  NextPoolSection,
  PinnedButtons,
  RulesCard,
  GuestTeaser,
} from "./KnockoutOverviewSections";


const SPORTS = [
  { id: "football", raw: "Football", hex: "#00b04f", icon: footballIcon },
  { id: "baseball", raw: "Baseball", hex: "#db2626", icon: baseballIcon },
  { id: "basketball", raw: "Basketball", hex: "#ff8c00", icon: basketballIcon },
  { id: "hockey", raw: "Hockey", hex: "#0da6e8", icon: hockeyIcon },
  { id: "soccer", raw: "Soccer", hex: "#1a66e6", icon: soccerIcon },
];

function sportMeta(id) {
  return SPORTS.find((s) => s.id === id) || SPORTS[0];
}

// Header row styling — mirrors the Pick Up landing's headerRow so the back button lands in
// the exact same spot (left), with the mode title centered and a matching-width spacer on the
// right (the champions trophy stays pinned separately via styles.championsWrap).
const HEADER_ROW = { display: "flex", alignItems: "center", paddingTop: 6, paddingBottom: 8 };
const BACK_BTN = {
  background: "none",
  border: "none",
  color: "rgba(255,255,255,0.8)",
  fontSize: 28,
  cursor: "pointer",
  width: 32,
  lineHeight: 1,
  textAlign: "left",
  flexShrink: 0,
};
const TITLE_CENTER = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  minWidth: 0,
  overflow: "hidden",
};

// How often the overview re-reads live data while open. The server only moves the
// cut line every 10 min, but other users' scores (alive count, top scores) change
// continuously during the draft window, so 30s keeps the screen feeling live
// without being heavy (~3 reads per tick for one active pool).
const REFRESH_INTERVAL_MS = 30000;


// ─── Screen ───────────────────────────────────────────────────────────────────

export default function Knockout() {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user) {
    return <GuestTeaser onSignIn={() => navigate("/login")} />;
  }
  return <KnockoutOverview user={user} />;
}

// ─── Signed-in overview ───────────────────────────────────────────────────────

function KnockoutOverview({ user }) {
  const navigate = useNavigate();
  const { startSession } = useKnockoutSession();
  const [selectedSport, setSelectedSport] = useState("football");
  const [hasAppeared, setHasAppeared] = useState(false);

  const [loading, setLoading] = useState(true);
  const [pools, setPools] = useState([]); // active pools
  const [lastPool, setLastPool] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [registrantCount, setRegistrantCount] = useState(0);
  const [registering, setRegistering] = useState(false);
  const [startingDraft, setStartingDraft] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const [showChampions, setShowChampions] = useState(false);
  // poolID → { day, standing }
  const [poolData, setPoolData] = useState({});
  // poolID → { open, loading, list }
  const [expanded, setExpanded] = useState({});

  const meta = sportMeta(selectedSport);

  useEffect(() => {
    const t = setTimeout(() => setHasAppeared(true), 60);
    return () => clearTimeout(t);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setExpanded({});
    try {
      const [activePools, last, reg, count] = await Promise.all([
        loadActivePools(selectedSport),
        loadLastCompletedPool(selectedSport),
        isRegistered(user.uid, selectedSport),
        countRegistrants(selectedSport),
      ]);

      setPools(activePools);
      setLastPool(last);
      setRegistered(reg);
      setRegistrantCount(count);

      // Per-pool day + standing.
      const perPool = {};
      await Promise.all(
        activePools.map(async (pool) => {
          const [day, standing] = await Promise.all([
            loadPoolDay(pool.id, pool.currentDay),
            loadStanding(pool.id, user.uid),
          ]);
          perPool[pool.id] = { day, standing };
        })
      );
      setPoolData(perPool);

      // Contract check: load the shared player pool for the first active pool so
      // the Pacific date key + compound-key decode are exercised against real
      // data (logged to console — see knockoutService.loadPlayerPool).
      if (activePools[0]) {
        await loadPlayerPool(selectedSport, activePools[0].poolSlot);
      }
    } catch (err) {
      console.error("🥊 Knockout: failed to load overview —", err);
      setPools([]);
      setLastPool(null);
      setPoolData({});
    } finally {
      setLoading(false);
    }
  }, [selectedSport, user.uid]);

  useEffect(() => {
    load();
  }, [load]);

  // Quiet live refresh: re-reads only the docs that change while the screen is
  // open (active pools, each pool's day + the user's standing). Unlike load() it
  // never sets `loading` (no dimming flash) and never clears `expanded` (an open
  // Today's Top Scores list stays open). Last-good data is kept on a failed tick.
  const refreshLive = useCallback(async () => {
    if (document.hidden) return; // don't poll a backgrounded tab
    try {
      const activePools = await loadActivePools(selectedSport);
      const perPool = {};
      await Promise.all(
        activePools.map(async (pool) => {
          const [day, standing] = await Promise.all([
            loadPoolDay(pool.id, pool.currentDay),
            loadStanding(pool.id, user.uid),
          ]);
          perPool[pool.id] = { day, standing };
        })
      );
      setPools(activePools);
      setPoolData(perPool);
    } catch (err) {
      console.error("🥊 Knockout: live refresh failed — keeping last data —", err);
    }
  }, [selectedSport, user.uid]);

  // Poll on an interval while the tab is visible, and refresh immediately when the
  // user returns to the tab so they never stare at stale numbers. The interval and
  // listener are both torn down on unmount / sport change (no leaked timers).
  useEffect(() => {
    const id = setInterval(refreshLive, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (!document.hidden) refreshLive();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshLive]);

  async function toggleStandings(poolID) {
    const cur = expanded[poolID];
    if (cur && cur.open) {
      setExpanded((e) => ({ ...e, [poolID]: { ...cur, open: false } }));
      return;
    }
    if (cur && cur.list) {
      setExpanded((e) => ({ ...e, [poolID]: { ...cur, open: true } }));
      return;
    }
    setExpanded((e) => ({ ...e, [poolID]: { open: true, loading: true, list: null } }));
    try {
      const standings = await loadStandings(poolID);
      const list = standings
        .filter((s) => s.status === "alive" && s.hasDraftedToday)
        .sort((a, b) => (b.currentDayScore ?? 0) - (a.currentDayScore ?? 0))
        .map((s, i) => ({
          rank: i + 1,
          username: s.username,
          score: s.currentDayScore ?? 0,
          isMe: s.userID === user.uid,
        }));
      setExpanded((e) => ({ ...e, [poolID]: { open: true, loading: false, list } }));
    } catch (err) {
      console.error("🥊 Knockout: failed to load standings —", err);
      setExpanded((e) => ({ ...e, [poolID]: { open: true, loading: false, list: [] } }));
    }
  }

  const appear = (delay) => ({
    opacity: hasAppeared ? 1 : 0,
    transform: hasAppeared ? "translateY(0)" : "translateY(8px)",
    transition: `opacity 0.3s ease ${delay}s, transform 0.3s ease ${delay}s`,
  });

  // Next pool number = highest known pool number for this sport + 1. Registration
  // opts the user into notifications for that upcoming pool (matches iOS).
  const nextPoolNumber =
    Math.max(0, ...pools.map((p) => p.poolNumber), lastPool?.poolNumber ?? 0) + 1;
  const nextPoolID = `${meta.raw}_${nextPoolNumber}`;

  async function handleRegisterToggle() {
    if (registering) return;
    setRegistering(true);
    const wasRegistered = registered;
    // Optimistic UI — flip immediately, reconcile with the server count after.
    setRegistered(!wasRegistered);
    setRegistrantCount((c) => Math.max(0, c + (wasRegistered ? -1 : 1)));
    try {
      if (wasRegistered) {
        await unregisterFromKnockout(user.uid, selectedSport);
      } else {
        await registerForKnockout(user.uid, selectedSport, nextPoolID);
      }
      const count = await countRegistrants(selectedSport);
      setRegistrantCount(count);
    } catch (err) {
      console.error("🥊 Knockout: register toggle failed —", err);
      setRegistered(wasRegistered); // revert
      try {
        setRegistrantCount(await countRegistrants(selectedSport));
      } catch {
        /* leave count as-is if the reconcile read also fails */
      }
    } finally {
      setRegistering(false);
    }
  }

  async function handleDraftNow() {
    if (startingDraft) return;
    const pool = [...pools].sort((a, b) => b.poolNumber - a.poolNumber)[0];
    const day = pool ? poolData[pool.id]?.day : null;
    if (!pool || !day) return;
    setStartingDraft(true);
    setDraftError(null);
    try {
      await startSession(pool.id, selectedSport, pool.poolSlot, day.dayNumber);
      navigate("/battles/knockout/draft", {
        state: {
          poolId: pool.id,
          sportId: selectedSport,
          slot: pool.poolSlot,
          dayNumber: day.dayNumber,
          poolTitle: pool.displayTitle,
        },
      });
      // success → navigating away; no need to reset startingDraft
    } catch (err) {
      if (err.code === "sessionExpired") {
        // PATH 2 forfeit already wrote a 0 — refresh so the UI reflects it.
        setDraftError(err.message);
        load();
      } else {
        console.error("🥊 Knockout: start draft failed —", err);
        setDraftError("Couldn't start draft. Please try again.");
      }
      setStartingDraft(false);
    }
  }

  return (
    <div style={styles.container}>
      {/* Champions (trophy) button — pinned to the top bar's right slot,
          mirroring the leaderboard champions button placement. */}
      <div style={styles.championsWrap}>
        <button
          style={styles.championsBtn}
          onClick={() => setShowChampions(true)}
          aria-label="Knockout champions"
          title="Champions"
        >
          <span
            style={{
              ...styles.championsIcon,
              WebkitMaskImage: `url(${trophyFill})`,
              maskImage: `url(${trophyFill})`,
            }}
          />
        </button>
      </div>

      {/* Title row — back button at left (same spot as the Pick Up landing), mode title
          centered, and a matching-width spacer at right so the title stays centered. */}
      <div style={{ ...HEADER_ROW, ...appear(0.04) }}>
        <button style={BACK_BTN} onClick={() => navigate("/battles")}>‹</button>
        <div style={TITLE_CENTER}>
          <span style={{ ...styles.treeIcon, transform: "rotate(180deg)" }} />
          <span style={styles.title}>KNOCKOUT MODE</span>
          <span style={{ ...styles.treeIcon, transform: "rotate(180deg)" }} />
        </div>
        <span style={{ width: 32, flexShrink: 0 }} />
      </div>

      {/* Sport selector */}
      <div style={{ ...styles.sportSelector, ...appear(0.07) }}>
        {SPORTS.map((s) => {
          const selected = s.id === selectedSport;
          return (
            <button
              key={s.id}
              onClick={() => setSelectedSport(s.id)}
              style={{
                ...styles.sportPill,
                background: selected ? s.hex : "rgba(255,255,255,0.07)",
                border: selected ? "none" : "1px solid rgba(255,255,255,0.12)",
                boxShadow: selected ? `0 4px 8px ${s.hex}73` : "none",
                transform: selected ? "scale(1.04)" : "scale(1)",
              }}
            >
              <span
                style={{
                  ...styles.sportGlyph,
                  background: selected ? "#000" : "rgba(255,255,255,0.7)",
                  WebkitMaskImage: `url(${s.icon})`,
                  maskImage: `url(${s.icon})`,
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Scroll content */}
      <div style={{ ...styles.content, opacity: loading ? 0.45 : 1, ...appear(0.1) }}>
        {lastPool && <LastPoolSection pool={lastPool} />}

        {[...pools]
          .sort((a, b) => a.poolNumber - b.poolNumber)
          .map((pool) => (
            <CurrentPoolSection
              key={pool.id}
              pool={pool}
              meta={meta}
              day={poolData[pool.id]?.day || null}
              standing={poolData[pool.id]?.standing || null}
              expanded={expanded[pool.id]}
              onToggleStandings={() => toggleStandings(pool.id)}
            />
          ))}

        <NextPoolSection
          meta={meta}
          registrantCount={registrantCount}
          registered={registered}
        />

        {pools.length === 0 && !loading && <RulesCard meta={meta} />}
      </div>

      {/* Pinned buttons — DRAFT NOW and REGISTER both live in Phase D */}
      <PinnedButtons
        pools={pools}
        poolData={poolData}
        registered={registered}
        registering={registering}
        onRegisterToggle={handleRegisterToggle}
        startingDraft={startingDraft}
        draftError={draftError}
        onDraftNow={handleDraftNow}
      />

      {showChampions && (
        <KnockoutChampionsModal onClose={() => setShowChampions(false)} />
      )}
    </div>
  );
}

// ─── Section header (label between two fading sport-colored rules) ────────────

